<script setup lang="ts">
import { Mail, MailOpen } from 'lucide-vue-next'
import { useNotificationHelpers } from '@/composables/useNotificationHelpers'
import { Button } from '@/foundation/button'
import type { Notification } from '@/types/notification'

type Props = {
  notification: Notification
  compact?: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  click: [notification: Notification]
  actionClick: [notification: Notification]
}>()

const { getNotificationIcon, getNotificationTypeLabel, getActionButtonText, isVeryRecent, formatTime } =
  useNotificationHelpers()

const handleClick = (notification: Notification) => {
  emit('click', notification)
}

const handleActionClick = (notification: Notification) => {
  emit('actionClick', notification)
}
</script>

<template>
  <div
    class="group cursor-pointer transition-all border border-border dark:border-border rounded-lg overflow-hidden"
    :class="{
      'bg-gray-100 dark:bg-gray-800/30 hover:bg-gray-200 dark:hover:bg-gray-800/50 hover:shadow-sm': !notification.readAt,
      'bg-gray-50 dark:bg-gray-900/20 hover:bg-gray-100 dark:hover:bg-gray-800/30 opacity-70': notification.readAt,
    }"
    @click="handleClick(notification)"
  >
    <div class="flex gap-3 p-3" :class="compact && 'py-2'">
      <div class="flex-shrink-0">
        <div class="flex h-8 w-8 items-center justify-center rounded-full bg-muted dark:bg-muted">
          <component
            :is="getNotificationIcon(notification.type)"
            class="h-4 w-4 text-muted-foreground dark:text-muted-foreground"
          />
        </div>
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-center justify-between gap-2 mb-1">
          <div class="flex items-center gap-1.5">
            <span class="text-xs font-medium text-muted-foreground dark:text-muted-foreground">
              {{ getNotificationTypeLabel(notification.type) }}
            </span>
            <span
              v-if="!notification.readAt && isVeryRecent(notification.createdAt)"
              class="flex h-2 w-2 rounded-full bg-red-500"
            />
          </div>
          <div class="flex items-center gap-1.5 flex-shrink-0">
            <span class="text-[10px] text-muted-foreground/70 dark:text-muted-foreground/70">
              {{ formatTime(notification.createdAt) }}
            </span>
            <component
              :is="notification.readAt ? MailOpen : Mail"
              :class="['h-3.5 w-3.5', notification.readAt ? 'text-muted-foreground/50' : 'text-primary']"
            />
          </div>
        </div>

        <h4 class="font-semibold text-xs mb-1 line-clamp-1">
          {{ notification.title }}
        </h4>

        <p
          class="text-xs text-muted-foreground dark:text-muted-foreground line-clamp-1"
          :class="!notification.readAt && 'font-medium'"
        >
          {{ notification.message }}
        </p>

        <div v-if="notification.actionUrl" class="mt-1">
          <Button
            variant="outline"
            size="sm"
            class="h-6 px-2 text-[10px] font-medium gap-1.5"
            @click.stop="handleActionClick(notification)"
          >
            <span class="h-2.5 w-2.5 rounded-full border border-current flex-shrink-0" />
            {{ getActionButtonText(notification.type) }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
